import React, { useEffect, useRef, useState } from 'react';
import {
  Copy,
  Printer,
  Bell,
  Truck,
  Camera,
  FileEdit,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const services = [
  {
    icon: Copy,
    title: 'Photocopy',
    description:
      'If you need copies of your original documents during the application process, our team can provide clear and high-quality photocopies on-site. This ensures your documents are ready and organised for submission.',
  },
  {
    icon: Printer,
    title: 'Printing',
    description:
      'Applicants are encouraged to bring printed copies of their applications and supporting documents. However, should you require assistance, ICAC offers printing facilities to ensure your documents are clear, professional, and compliant with Consular requirements.',
  },
  {
    icon: Bell,
    title: 'SMS & Email Alerts',
    description:
      'Stay updated throughout your application journey with our SMS and email notification service. You will receive timely updates as your application progresses, provided the contact details submitted are accurate and active.',
  },
  {
    icon: Truck,
    title: 'Courier Service',
    description:
      'For added convenience, applicants can choose to have passports and documents delivered directly to their home or office. Your preferred delivery address is recorded at the time of submission, and once dispatched, you will be provided with a tracking reference to monitor delivery.',
  },
  {
    icon: Camera,
    title: 'Photography',
    description:
      'ICAC provides professional photography services that meet Consular standards. This ensures your application is complete even if you do not have suitable photographs with you at the time of submission.',
  },
  {
    icon: FileEdit,
    title: 'Form Filling Assistance',
    description:
      'Applicants who prefer guided support may opt for form-filling assistance at the ICAC. Our staff will help complete your application form accurately based on the details you provide, reducing the chances of errors or omissions.',
  },
];

const ServiceCard = ({ service, index }) => {
  const { icon: Icon, title, description } = service;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="service-card"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.55s ease ${index * 0.08}s, transform 0.55s ease ${index * 0.08}s`,
      }}
    >
      <div className="card-icon-wrap">
        <Icon size={20} strokeWidth={1.6} />
      </div>
      <div className="card-body">
        <h3 className="card-title">{title}</h3>
        <p className="card-desc">{description}</p>
      </div>
      <div className="card-number">0{index + 1}</div>
    </div>
  );
};

const OptionalServices = () => {
  const heroRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');

        :root {
          --navy: #162b56;
          --navy-mid: #162b56;
          --navy-light: #1e3245;
          --gold: #c9a84c;
          --gold-light: #e8c96d;
          --gold-faint: rgba(201,168,76,0.08);
          --text-primary: #1a2e3d;
          --text-secondary: #4a6475;
          --text-muted: #7a98a8;
          --border: rgba(15,31,46,0.1);
          --card-bg: #ffffff;
          --page-bg: #f4f7f9;
        }

        .os-page {
          background: var(--page-bg);
          min-height: 100vh;
          font-family: 'DM Sans', sans-serif;
          color: var(--text-primary);
        }

        /* ── Hero ── */
        .os-hero {
          background: var(--navy);
          position: relative;
          overflow: hidden;
          padding: 80px 32px 72px;
        }

        .os-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 80% at 90% 50%, rgba(201,168,76,0.07) 0%, transparent 60%),
            radial-gradient(ellipse 40% 60% at 10% 80%, rgba(201,168,76,0.04) 0%, transparent 55%);
        }

        .os-hero::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, var(--gold), var(--gold-light), var(--gold), transparent);
        }

        .os-hero-inner {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .os-hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 18px;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s;
        }

        .os-hero-eyebrow.visible {
          opacity: 1; transform: translateY(0);
        }

        .os-hero-eyebrow::before {
          content: '';
          display: block;
          width: 28px; height: 1px;
          background: var(--gold);
        }

        .os-hero-title {
          font-family: 'Lora', serif;
          font-size: clamp(30px, 4vw, 46px);
          font-weight: 500;
          color: #d9e8f0;
          line-height: 1.22;
          margin-bottom: 20px;
          max-width: 640px;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.55s ease 0.2s, transform 0.55s ease 0.2s;
        }

        .os-hero-title.visible {
          opacity: 1; transform: translateY(0);
        }

        .os-hero-title em {
          font-style: italic;
          color: var(--gold-light);
        }

        .os-hero-subtitle {
          font-size: 14px;
          font-weight: 400;
          color: #7a98a8;
          line-height: 1.75;
          max-width: 560px;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 0.55s ease 0.32s, transform 0.55s ease 0.32s;
        }

        .os-hero-subtitle.visible {
          opacity: 1; transform: translateY(0);
        }

        /* ── Stats bar ── */
        .os-stats-bar {
          background: var(--navy-mid);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .os-stats-inner {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 32px;
          display: flex;
          align-items: stretch;
        }

        .os-stat {
          flex: 1;
          padding: 18px 24px;
          text-align: center;
          border-right: 1px solid rgba(255,255,255,0.05);
          transition: background 0.2s;
        }

        .os-stat:last-child { border-right: none; }
        .os-stat:hover { background: rgba(201,168,76,0.05); }

        .os-stat-value {
          font-family: 'Lora', serif;
          font-size: 22px;
          font-weight: 500;
          color: var(--gold-light);
          display: block;
          line-height: 1;
          margin-bottom: 4px;
        }

        .os-stat-label {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #546e7a;
        }

        @media (max-width: 640px) {
          .os-stats-bar { display: none; }
        }

        /* ── Body ── */
        .os-body {
          max-width: 1100px;
          margin: 0 auto;
          padding: 64px 32px 80px;
        }

        .os-section-header {
          margin-bottom: 48px;
        }

        .os-section-label {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .os-section-label::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(201,168,76,0.2);
          max-width: 80px;
        }

        .os-section-heading {
          font-family: 'Lora', serif;
          font-size: 24px;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 10px;
        }

        .os-section-intro {
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.75;
          max-width: 620px;
        }

        /* ── Grid ── */
        .os-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        @media (max-width: 900px) {
          .os-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 560px) {
          .os-grid { grid-template-columns: 1fr; }
          .os-body { padding: 40px 20px 60px; }
          .os-hero { padding: 56px 20px 52px; }
        }

        /* ── Service Card ── */
        .service-card {
          background: var(--card-bg);
          border-radius: 12px;
          border: 1px solid var(--border);
          padding: 28px 24px 24px;
          position: relative;
          overflow: hidden;
          cursor: default;
          transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--gold), var(--gold-light));
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }

        .service-card:hover {
          border-color: rgba(201,168,76,0.3);
          box-shadow: 0 8px 32px rgba(15,31,46,0.1);
          transform: translateY(-3px);
        }

        .service-card:hover::before {
          transform: scaleX(1);
        }

        .card-icon-wrap {
          width: 44px; height: 44px;
          border-radius: 10px;
          background: var(--gold-faint);
          border: 1px solid rgba(201,168,76,0.18);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gold);
          margin-bottom: 18px;
          transition: background 0.25s, border-color 0.25s;
        }

        .service-card:hover .card-icon-wrap {
          background: rgba(201,168,76,0.14);
          border-color: rgba(201,168,76,0.35);
        }

        .card-body { position: relative; z-index: 1; }

        .card-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 10px;
          letter-spacing: -0.01em;
        }

        .card-desc {
          font-size: 13px;
          font-weight: 400;
          color: var(--text-secondary);
          line-height: 1.72;
        }

        .card-number {
          position: absolute;
          bottom: 18px;
          right: 20px;
          font-family: 'Lora', serif;
          font-size: 38px;
          font-weight: 500;
          color: rgba(15,31,46,0.04);
          line-height: 1;
          user-select: none;
          pointer-events: none;
          transition: color 0.25s;
        }

        .service-card:hover .card-number {
          color: rgba(201,168,76,0.08);
        }

        /* ── Note Banner ── */
        .os-note {
          margin-top: 48px;
          background: linear-gradient(135deg, rgba(201,168,76,0.07) 0%, rgba(201,168,76,0.03) 100%);
          border: 1px solid rgba(201,168,76,0.2);
          border-radius: 12px;
          padding: 24px 28px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .os-note-icon {
          width: 36px; height: 36px;
          flex-shrink: 0;
          background: rgba(201,168,76,0.12);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gold);
          font-family: 'Lora', serif;
          font-size: 16px;
          font-weight: 600;
          font-style: italic;
        }

        .os-note-text {
          flex: 1;
        }

        .os-note-title {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 5px;
        }

        .os-note-body {
          font-size: 13px;
          color: var(--text-secondary);
          line-height: 1.7;
        }
      `}</style>

      <Header />

      <div className="os-page">

        {/* ── Hero ── */}
        <section className="os-hero">
          <div className="os-hero-inner">
            <h1 className={`os-hero-title ${heroVisible ? 'visible' : ''}`}>
              Optional <em>Services</em><br />at ICAC
            </h1>
            <p className={`os-hero-subtitle ${heroVisible ? 'visible' : ''}`}>
              At the India Consular Application Centre (ICAC), we aim to provide applicants
              with a smooth and convenient experience. In addition to the core application
              process, applicants may also benefit from the following support services.
            </p>
          </div>
        </section>

       

        {/* ── Services ── */}
        <div className="os-body">
         
          <div className="os-grid">
            {services.map((service, i) => (
              <ServiceCard key={service.title} service={service} index={i} />
            ))}
          </div>

        </div>

      </div>

      <Footer />
    </>
  );
};

export default OptionalServices;